(() => {
  const VALID_OUTCOMES = ['granted', 'full'];

  const read = (storage, key) => {
    try { return storage.getItem(key); } catch (error) { return null; }
  };

  const write = (storage, key, value = 'true') => {
    try { storage.setItem(key, value); } catch (error) {}
  };

  const lockPage = (locked) => {
    document.documentElement.classList.toggle('birdega-back-room-lock', locked);
    document.body.classList.toggle('birdega-back-room-lock', locked);
  };

  const init = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const dialog = root.querySelector('[data-back-room-dialog]');
    const openButtons = Array.from(root.querySelectorAll('[data-back-room-open]'));
    const closeButton = root.querySelector('[data-back-room-close]');
    const ticket = root.querySelector('[data-back-room-ticket]');
    const canvas = root.querySelector('[data-back-room-canvas]');
    const scratchWrap = root.querySelector('[data-back-room-scratch-wrap]');
    const revealButton = root.querySelector('[data-back-room-reveal]');
    const focusEmailButton = root.querySelector('[data-back-room-focus-email]');
    const instruction = root.querySelector('.birdega-back-room__instruction');
    const success = root.querySelector('[data-back-room-success]');
    const postedSuccess = root.querySelector('[data-back-room-posted-success]');
    const errors = root.querySelector('[data-back-room-errors]');
    const persistenceError = root.querySelector('[data-back-room-persistence-error]');

    if (!dialog || !openButtons.length || !ticket || !canvas || !scratchWrap) return;

    let activeOpenButton = openButtons[0];

    const storageKey = root.dataset.outcomeStorageKey || 'birdega_employee_access_001';
    const grantSubmittedKey = storageKey + '_granted_submitted';
    const onCallSubmittedKey = storageKey + '_oncall_submitted';
    const onCallPendingEmailKey = storageKey + '_oncall_pending_email';
    const issueNumber = Number.parseInt(root.dataset.issueNumber || '1', 10);
    const outcomeEndpoint = root.dataset.outcomeEndpoint || '/apps/employees-only/outcome';
    const configuredPercentage = Number.parseFloat(root.dataset.accessPercentage);
    const accessRate = Math.min(1, Math.max(0, Number.isFinite(configuredPercentage) ? configuredPercentage / 100 : 0.25));
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const coating = root.dataset.scratchColor || '#aaa9a4';

    let context;
    let drawing = false;
    let lastPoint = null;
    let revealed = false;
    let lastCheck = 0;
    let prepared = false;
    let currentOutcome = null;
    let canvasMetrics = null;
    let prepareFrame = null;
    let resizeFrame = null;

    const redirectedSuccess = (() => {
      try {
        const params = new URLSearchParams(window.location.search);
        const outcome = params.get('employee_access_submitted');
        return VALID_OUTCOMES.includes(outcome) ? outcome : null;
      } catch (error) {
        return null;
      }
    })();

    const clearSuccessMarker = () => {
      if (!redirectedSuccess || !window.history?.replaceState) return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('employee_access_submitted');
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      } catch (error) {}
    };

    const setStoredOutcome = (outcome) => {
      if (!VALID_OUTCOMES.includes(outcome)) return;
      currentOutcome = outcome;
      write(localStorage, storageKey, outcome);
    };

    const getEmployeeAccessOutcome = () => {
      if (currentOutcome) return currentOutcome;

      if (read(localStorage, grantSubmittedKey) === 'true') {
        setStoredOutcome('granted');
        return currentOutcome;
      }

      if (read(localStorage, onCallSubmittedKey) === 'true') {
        setStoredOutcome('full');
        return currentOutcome;
      }

      const stored = read(localStorage, storageKey);
      if (VALID_OUTCOMES.includes(stored)) {
        currentOutcome = stored;
        return currentOutcome;
      }

      // Preserve and migrate outcomes created before Issue 001 moved from
      // session-only persistence to browser-persistent storage.
      const legacySessionOutcome = read(sessionStorage, storageKey);
      if (VALID_OUTCOMES.includes(legacySessionOutcome)) {
        setStoredOutcome(legacySessionOutcome);
        return currentOutcome;
      }

      setStoredOutcome(Math.random() < accessRate ? 'granted' : 'full');
      return currentOutcome;
    };

    const prepareCanvas = () => {
      if (revealed) return false;
      const rect = scratchWrap.getBoundingClientRect();
      if (!rect.width || !rect.height) return false;

      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.round(rect.width);
      const height = Math.round(rect.height);
      const backingWidth = Math.round(width * ratio);
      const backingHeight = Math.round(height * ratio);
      const sizeChanged = !canvasMetrics ||
        canvasMetrics.width !== width ||
        canvasMetrics.height !== height ||
        canvasMetrics.ratio !== ratio;

      if (prepared && !sizeChanged) return true;

      canvas.classList.remove('is-ready');
      canvas.dataset.scratchInitialized = 'false';

      if (canvas.width !== backingWidth) canvas.width = backingWidth;
      if (canvas.height !== backingHeight) canvas.height = backingHeight;

      context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return false;

      context.globalCompositeOperation = 'source-over';
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.fillStyle = coating;
      context.fillRect(0, 0, width, height);
      context.fillStyle = 'rgba(17,17,17,.74)';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.font = '600 11px Arial, sans-serif';
      context.fillText('SCRATCH TO CLOCK IN', width / 2, height / 2);
      context.strokeStyle = 'rgba(255,255,255,.12)';
      context.lineWidth = 1;

      for (let y = 12; y < height; y += 13) {
        context.beginPath();
        context.moveTo(0, y);
        context.lineTo(width, y);
        context.stroke();
      }

      canvasMetrics = { width, height, ratio };
      prepared = true;
      canvas.dataset.scratchInitialized = 'true';
      canvas.classList.add('is-ready');
      scratchWrap.classList.add('is-canvas-ready');
      return true;
    };

    const schedulePrepare = () => {
      if (revealed) return;
      if (prepareFrame) window.cancelAnimationFrame(prepareFrame);
      prepareFrame = window.requestAnimationFrame(() => {
        prepareFrame = null;
        if (!prepareCanvas() && dialog.open) schedulePrepare();
      });
    };

    const resetScratchSurface = () => {
      if (revealed) return;
      prepared = false;
      canvasMetrics = null;
      context = null;
      drawing = false;
      lastPoint = null;
      canvas.dataset.scratchInitialized = 'false';
      canvas.classList.remove('is-ready');
      scratchWrap.classList.remove('is-canvas-ready');
    };

    const renderOutcome = (outcome) => {
      root.querySelectorAll('[data-outcome-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.outcomePanel !== outcome;
      });

      root.querySelectorAll('[data-back-room-claim]').forEach((claim) => {
        claim.hidden = outcome === 'granted' || claim.dataset.backRoomClaim !== outcome;
      });
    };

    const showSuccess = (outcome) => {
      setStoredOutcome(outcome);
      revealed = true;
      scratchWrap.hidden = true;
      revealButton.hidden = true;
      if (instruction) instruction.hidden = true;
      root.querySelectorAll('[data-back-room-claim]').forEach((claim) => { claim.hidden = true; });
      root.querySelectorAll('[data-back-room-success-state]').forEach((state) => {
        state.hidden = state.dataset.backRoomSuccessState !== outcome;
      });
      success.hidden = false;
      ticket.classList.add('is-success');
      ticket.classList.remove('is-revealed');

      if (outcome === 'granted') write(localStorage, grantSubmittedKey);
      else write(localStorage, onCallSubmittedKey);

      window.setTimeout(() => success.focus(), 0);
    };

    const showPersistenceError = () => {
      setStoredOutcome('full');
      revealed = true;
      scratchWrap.hidden = true;
      revealButton.hidden = true;
      if (instruction) instruction.hidden = true;
      success.hidden = true;
      ticket.classList.remove('is-success');
      ticket.classList.add('is-revealed');
      root.querySelectorAll('[data-back-room-claim]').forEach((claim) => {
        claim.hidden = claim.dataset.backRoomClaim !== 'full';
      });
      if (persistenceError) persistenceError.hidden = false;
      window.setTimeout(() => persistenceError?.focus(), 0);
    };

    const confirmOnCall = async () => {
      const email = read(sessionStorage, onCallPendingEmailKey);
      if (!email) {
        showPersistenceError();
        return;
      }

      try {
        const response = await fetch(`${outcomeEndpoint}?issue=${encodeURIComponent(issueNumber)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ issue: issueNumber, email, outcome: 'on-call' }),
          credentials: 'same-origin'
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || payload?.ok !== true || payload?.outcome !== 'on-call') throw new Error('outcome_not_persisted');
        sessionStorage.removeItem(onCallPendingEmailKey);
        if (persistenceError) persistenceError.hidden = true;
        showSuccess('full');
      } catch (error) {
        showPersistenceError();
      }
    };

    const completeReveal = () => {
      if (revealed) return;
      const outcome = getEmployeeAccessOutcome();
      revealed = true;
      renderOutcome(outcome);
      ticket.classList.add('is-revealed');
      revealButton.hidden = true;
      canvas.style.opacity = '0';
      canvas.style.pointerEvents = 'none';
      window.setTimeout(() => { canvas.hidden = true; }, reducedMotion ? 0 : 190);

      if (outcome === 'full') {
        const onCallEmail = root.querySelector('[data-back-room-claim="full"] input[type="email"]');
        window.setTimeout(() => onCallEmail?.focus(), reducedMotion ? 0 : 220);
      }
    };

    const scratchedPercent = () => {
      if (!context || !canvas.width || !canvas.height || revealed) return revealed ? 1 : 0;
      const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
      const step = Math.max(8, Math.floor(canvas.width / 150));
      let clear = 0;
      let total = 0;

      for (let y = 0; y < canvas.height; y += step) {
        for (let x = 0; x < canvas.width; x += step) {
          total += 1;
          if (pixels[(y * canvas.width + x) * 4 + 3] < 80) clear += 1;
        }
      }

      return total ? clear / total : 0;
    };

    const checkThreshold = (force = false) => {
      if (revealed) return;
      const now = performance.now();
      if (!force && now - lastCheck < 90) return;
      lastCheck = now;
      if (scratchedPercent() >= 0.60) completeReveal();
    };

    const pointFromEvent = (event) => {
      const rect = canvas.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };

    const scratchTo = (point) => {
      if (!context || revealed) return;
      const rect = canvas.getBoundingClientRect();
      context.globalCompositeOperation = 'destination-out';
      context.lineWidth = Math.max(30, Math.min(54, rect.width * 0.085));
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.beginPath();
      if (lastPoint) context.moveTo(lastPoint.x, lastPoint.y);
      else context.moveTo(point.x, point.y);
      context.lineTo(point.x, point.y);
      context.stroke();
      lastPoint = point;
    };

    const open = (event) => {
      if (event?.currentTarget) activeOpenButton = event.currentTarget;
      renderOutcome(getEmployeeAccessOutcome());

      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }

      lockPage(true);
      window.requestAnimationFrame(() => {
        if (!prepared && !revealed) schedulePrepare();
        closeButton?.focus();
      });
    };

    const close = () => {
      write(sessionStorage, storageKey + '_dismissed');
      resetScratchSurface();
      lockPage(false);
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
      activeOpenButton?.focus();
    };

    canvas.addEventListener('pointerdown', (event) => {
      if (!prepared || revealed) return;
      event.preventDefault();
      drawing = true;
      lastPoint = null;
      canvas.setPointerCapture?.(event.pointerId);
      scratchTo(pointFromEvent(event));
    });

    canvas.addEventListener('pointermove', (event) => {
      if (!prepared || !drawing || revealed) return;
      event.preventDefault();
      scratchTo(pointFromEvent(event));
      checkThreshold();
    });

    const stopDrawing = (event) => {
      if (!drawing) return;
      drawing = false;
      lastPoint = null;
      if (canvas.hasPointerCapture?.(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
      checkThreshold(true);
    };

    canvas.addEventListener('pointerup', stopDrawing);
    canvas.addEventListener('pointercancel', stopDrawing);
    canvas.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') stopDrawing(event);
    });

    // Some mobile Safari/WebView contexts expose Pointer Events but do not
    // reliably deliver pointermove while a modal canvas is active. Keep a
    // direct touch path so finger scratching remains responsive there.
    const pointFromTouch = (touch) => {
      const rect = canvas.getBoundingClientRect();
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    };

    canvas.addEventListener('touchstart', (event) => {
      if (!prepared || revealed || !event.touches.length) return;
      event.preventDefault();
      drawing = true;
      lastPoint = null;
      scratchTo(pointFromTouch(event.touches[0]));
    }, { passive: false });

    canvas.addEventListener('touchmove', (event) => {
      if (!prepared || !drawing || revealed || !event.touches.length) return;
      event.preventDefault();
      scratchTo(pointFromTouch(event.touches[0]));
      checkThreshold();
    }, { passive: false });

    canvas.addEventListener('touchend', (event) => {
      if (!drawing) return;
      event.preventDefault();
      drawing = false;
      lastPoint = null;
      checkThreshold(true);
    }, { passive: false });

    canvas.addEventListener('touchcancel', () => {
      drawing = false;
      lastPoint = null;
    }, { passive: false });

    openButtons.forEach((button) => button.addEventListener('click', open));
    closeButton?.addEventListener('click', close);
    revealButton?.addEventListener('click', completeReveal);
    focusEmailButton?.addEventListener('click', () => {
      const grantedClaim = root.querySelector('[data-back-room-claim="granted"]');
      const grantedEmail = grantedClaim?.querySelector('input[type="email"]');
      grantedClaim.hidden = false;
      window.setTimeout(() => grantedEmail?.focus(), 0);
    });

    const onCallForm = root.querySelector('[data-back-room-claim="full"] form');
    onCallForm?.addEventListener('submit', () => {
      const email = onCallForm.querySelector('input[type="email"]')?.value.trim().toLowerCase();
      if (email) write(sessionStorage, onCallPendingEmailKey, email);
      if (persistenceError) persistenceError.hidden = true;
    });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });

    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right &&
        event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) close();
    });

    window.addEventListener('resize', () => {
      if (!dialog.open || revealed || drawing) return;
      if (resizeFrame) window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = null;
        prepareCanvas();
      });
    }, { passive: true });

    if (redirectedSuccess) {
      clearSuccessMarker();
      open();
      if (redirectedSuccess === 'full') confirmOnCall();
      else showSuccess(redirectedSuccess);
    } else if (postedSuccess) {
      const outcome = postedSuccess.dataset.backRoomPostedSuccess;
      open();
      if (outcome === 'full') confirmOnCall();
      else showSuccess(VALID_OUTCOMES.includes(outcome) ? outcome : getEmployeeAccessOutcome());
    } else if (errors) {
      const outcome = errors.dataset.backRoomErrors;
      setStoredOutcome(VALID_OUTCOMES.includes(outcome) ? outcome : getEmployeeAccessOutcome());
      completeReveal();
      if (currentOutcome === 'granted') {
        const grantedClaim = root.querySelector('[data-back-room-claim="granted"]');
        if (grantedClaim) grantedClaim.hidden = false;
      }
      open();
    } else if (read(localStorage, grantSubmittedKey) === 'true') {
      showSuccess('granted');
    } else if (read(localStorage, onCallSubmittedKey) === 'true') {
      showSuccess('full');
    }

    document.addEventListener('shopify:section:select', (event) => {
      if (event.target?.contains(root)) open();
    });
  };

  const boot = () => document.querySelectorAll('[data-back-room]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
